import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import withMemo from '../../decorators/WithMemo'
import { generateFieldsProps, getFieldTranslations } from '../../helpers/FormHelpers'
import { actions as OfferActions, selectors as OfferSelectors } from '../../pages/Offer/redux'
import { selectors as AuthSelectors } from '../../redux/auth/redux'
import { actions as chatActions } from '../../redux/chat/redux'
import { fields } from '../OfferApplyForm'
import { api } from '../../config'
import {
  getPubNubInstance,
  setUserMetadata,
  setUserMetadataForBusiness,
  setUserMetadataForExternalBusiness,
  generateChannelOfferID,
  setMembershipToChannel,
  getMembershipToChannel,
  getChannelMembers,
  getUserMetadata,
  generateChannelMetadata
} from '../../helpers/PubNubHelpers'

import OfferDetails from './index'


const OfferDetailsWrapper = (props) => {
  const { offer } = props
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const user = useSelector(AuthSelectors.user)
  const isOwner = useMemo(() => !!user?.offers?.find((o) => o.id === offer?.id), [offer, user])
  const appliedAt = useMemo(() => user?.candidacies.find((c) => c.offer?.id === offer?.id)?.appliedAt, [offer, user])
  const isPremium = useSelector(AuthSelectors.isActivePremium)
  const isConnected = useSelector(AuthSelectors.isConnected)
  const token = useSelector(AuthSelectors.token)

  const applyToOffer = useSelector(OfferSelectors.applyToOffer)
  const handleApply = useCallback((values) => {
    dispatch(OfferActions.applyToOffer({ offer: offer?.id, message: values.message, files: values.files }))
  }, [dispatch, offer])

  const fileUploadEndpoint = api.UPLOAD_ENDPOINT_FILE

  const applyFormProps = useMemo(() => ({
    submitLabel: t('offerDetails_submitLabel'),
    fieldsProps: generateFieldsProps(fields, (field) => {
      const fieldProps = getFieldTranslations(t, `offerDetails_form_${field.name}`)

      if (field.name === 'files') {
        fieldProps.text = t('offerDetails_form_files_text')
        fieldProps.browseLabel = t('offerDetails_form_files_browse')
        fieldProps.nameLabel = t('offerDetails_form_files_name')
        fieldProps.previewLabel = t('offerDetails_form_files_preview')
        fieldProps.deleteLabel = t('offerDetails_form_files_delete')
        fieldProps.legend = t('offerDetails_form_files_legend')
        fieldProps.config = {
          endpoint: fileUploadEndpoint,
          token: token?.access_token,
          maxFileSize: 1024 * 1024 * 10,
          maxFiles: 3,
          testChunks: false,
          simultaneousUploads: 1,
        }
      }

      return fieldProps
    }),
    messages: {
      required: t('form_validation_required'),
    },
    onSubmit: handleApply,
    isPending: applyToOffer.pending,
    isSuccess: applyToOffer.success,
    errors: applyToOffer.errors,
  }), [applyToOffer.errors, applyToOffer.pending, applyToOffer.success, fileUploadEndpoint, handleApply, t, token])

  // CHAT

  const [chatOpen, setChatOpen] = useState(false)
  const [pubnub, setPubnub] = useState(null)
  const [currentChannel, setCurrentChannel] = useState(null)
  const [membersOfChannel, setMembersOfChannel] = useState(null)
  const [currentChannelMeta, setCurrentChannelMeta] = useState(null)
  const [userChannelsWithMeta, setUserChannelsWithMeta] = useState(null)

  const onChannelSwitched = useCallback((channel) => {
    if (channel) {
      setCurrentChannel(channel.id)
      let currentChannelWithMeta = userChannelsWithMeta.filter((c) => c.id === channel.id)

      currentChannelWithMeta = { ...currentChannelWithMeta[0], unreadCount: 0 }

      setUserChannelsWithMeta(userChannelsWithMeta.map((c) => [currentChannelWithMeta].find((cc) => cc.id === c.id) || c))
    }
  }, [userChannelsWithMeta])

  const onSendMessage = useCallback(() => {
    if (user) {
      dispatch(chatActions.sendNotification({ channelID: currentChannel, userID: user.id }))
    }
  }, [currentChannel, dispatch, user])


  const openChat = useCallback(() => {
    setChatOpen(true)
  }, [])

  useEffect(() => {
    // eslint-disable-next-line complexity
    (async() => {
      if (user && chatOpen) {
        const UUID = isOwner
          ? `offer_${offer.id}`
          : `${user.id}_${user.first_name.replace(/\s/g, '')}_${user.last_name.replace(/\s/g, '')}`

        const localPubnub = getPubNubInstance(UUID, true)

        let localChannelID = currentChannel !== null ? currentChannel : ''

        let currentMembership

        setPubnub(localPubnub)
        if (localPubnub) {
          if (isOwner) {
            setUserMetadataForBusiness(localPubnub, UUID, offer)
          } else {
            setUserMetadata(localPubnub, UUID, user)
          }

          if (!isOwner) {
            const uuidOther = `offer_${offer.id}`
            const uuidAdministrator = `${offer.user.id}_${offer.user.first_name.replace(/\s/g, '')}_${offer.user.last_name.replace(/\s/g, '')}`

            await setUserMetadataForExternalBusiness(uuidOther, offer)
            await setUserMetadataForExternalBusiness(uuidAdministrator, offer)

            const channelID = generateChannelOfferID(user, offer)

            currentMembership = await setMembershipToChannel(localPubnub, UUID, channelID)
            await setMembershipToChannel(localPubnub, uuidOther, channelID) // register to channel offer
            await setMembershipToChannel(localPubnub, uuidAdministrator, channelID) // register to channel admin

            if (localChannelID.length === 0) {
              setCurrentChannel(channelID)
              localChannelID = channelID
            }
          } else if (isOwner && currentChannel) {
            await setMembershipToChannel(localPubnub, UUID, currentChannel)
          }

          const userMembership = await getMembershipToChannel(localPubnub, UUID)

          let localUserMembership

          // Set userChannels and current Channel
          if (userMembership && userMembership.data && userMembership.data.length > 0) {
            // Make sure that the newest channel is added
            // because sometimes the membership data is not fully setted when we call the getter for the membership
            if (currentMembership && !userMembership.data.some((c) => c.channel.id === currentMembership.channel.id)) {
              userMembership.data.push(currentMembership)
            }
            localUserMembership = userMembership.data.map((c) => ({ id: c.channel.id, timetoken: c?.custom?.lastReadTimetoken || '' })).filter((c) => !c.id.includes('NaN') && c.id.includes('offer'))
            if (isOwner && localChannelID.length === 0) {
              setCurrentChannel(userMembership.data[0].channel.id)
              localChannelID = userMembership.data[0].channel.id
            }
          }

          // Set members of channels with metadata to display
          if (localChannelID.length > 0) {
            const members = await getChannelMembers(localPubnub, localChannelID)

            if (members) {
              const membersWithMeta = await Promise.all(
                members.data.map((m) => getUserMetadata(localPubnub, m.uuid.id))
              )

              if (membersWithMeta) {
                setMembersOfChannel(membersWithMeta.map((m) => m.data))
              }
            }

            // Set channels with metadata
            const localChannelsWithMetadata = await generateChannelMetadata(localPubnub, localUserMembership, UUID)

            if (localChannelsWithMetadata) {
              let localCurrentChannelWithMeta = localChannelsWithMetadata.filter((c) => c.id === localChannelID)[0]

              localCurrentChannelWithMeta = { ...localCurrentChannelWithMeta, unreadCount: 0 }

              setCurrentChannelMeta(localCurrentChannelWithMeta)
              if (userChannelsWithMeta === null) {
                // eslint-disable-next-line max-len
                setUserChannelsWithMeta(localChannelsWithMetadata.map((c) => [localCurrentChannelWithMeta].find((cc) => cc.id === c.id) || c))
              }
            }
          }
        }
      }
    })()
  }, [chatOpen, currentChannel, isOwner, offer, user, userChannelsWithMeta])

  // update redux currentChannel
  useEffect(() => {
    dispatch(chatActions.setCurrentChannel(currentChannel))
  }, [currentChannel, dispatch])

  // quit current channel on unmount
  useEffect(() => () => dispatch(chatActions.setCurrentChannel(null)), [dispatch])


  const chat = useMemo(() => ({
    pubnub,
    currentChannel,
    membersOfChannel,
    currentChannelMeta,
    userChannelsWithMeta,
    onChannelSwitched,
    onSendMessage,
  // eslint-disable-next-line max-len
  }), [currentChannel, currentChannelMeta, membersOfChannel, onChannelSwitched, onSendMessage, pubnub, userChannelsWithMeta])

  return (
    <OfferDetails
      isConnected={isConnected}
      isPremium={isPremium}
      isOwner={isOwner}
      appliedAt={appliedAt}
      onApply={handleApply}
      offer={offer}
      applyFormProps={applyFormProps}
      contactLabel={isOwner ? t('offerDetails_seeMyMessage') : t('offerDetails_contactAuthor')}
      openChat={openChat}
      chat={chat}
    />
  )
}

OfferDetailsWrapper.propTypes = OfferDetails.propTypes

export default withMemo()(OfferDetailsWrapper)
